import { useEffect, useState } from "react";
import { apiTech } from "@/lib/api";
import { useRealtime } from "@/lib/realtime";
import { toast } from "sonner";
import { StatusPill, EmptyState } from "@/components/admin";
import SwipeCard from "@/components/SwipeCard";
import { ChevronRight, MapPin, Clock, Flag, Phone, Truck, MapPinned } from "lucide-react";

const STATUS_ORDER = ["on-site", "en-route", "scheduled", "new"];
const STATUS_LABELS = {
  "on-site": "On site",
  "en-route": "En-route",
  "scheduled": "Scheduled",
  "new": "New",
};

export default function TechToday({ onOpenJob }) {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  function reload() {
    return Promise.all([
      apiTech.get("/tech/jobs").then((r) => r.data),
      apiTech.get("/tech/me/stats").then((r) => r.data),
    ])
      .then(([j, s]) => { setJobs(j); setStats(s); setLoading(false); })
      .catch(() => { setLoading(false); });
  }

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useRealtime("tech", ["job."], reload);

  async function flipStatus(jobId, endpoint, label) {
    try {
      await apiTech.post(`/tech/jobs/${jobId}/${endpoint}`);
      toast.success(label);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  }

  // Group by status (in priority order)
  const groups = STATUS_ORDER
    .map((s) => [s, jobs.filter((j) => j.status === s)])
    .filter(([, list]) => list.length > 0);

  const activeCount = jobs.filter((j) => j.status === "en-route" || j.status === "on-site").length;

  return (
    <>
      <div className="grid grid-cols-3 gap-2.5">
        <Tile label="Open" value={jobs.length} testid="tech-stat-open" />
        <Tile label="Active" value={activeCount} tone="amber" testid="tech-stat-active" />
        <Tile
          label="Today $"
          value={stats ? `$${Math.round(stats.today_revenue)}` : "—"}
          tone="emerald"
          testid="tech-stat-revenue"
        />
      </div>

      <div className="flex items-end justify-between pt-3 pb-1">
        <div>
          <div className="text-base font-semibold text-[#FFFFFF]">Open jobs</div>
          <div className="text-[11px] text-[#FFFFFF]/70">
            <span className="text-[#FFFFFF] font-medium">← swipe right to call</span> · <span className="text-[#FF6600] font-medium">swipe left to advance →</span>
          </div>
        </div>
        <div className="text-[11px] text-[#FFFFFF]/45 num">{jobs.length} total</div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-28 bg-[#002060] border border-[#FFFFFF]/15 rounded-lg animate-pulse" />
          ))}
        </div>
      ) : jobs.length === 0 ? (
        <div className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg">
          <EmptyState message="Nothing open. Nice work." />
        </div>
      ) : (
        groups.map(([status, items]) => (
          <section key={status} className="mt-3 first:mt-2">
            <header className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <StatusPill status={status} />
                <span className="text-[11px] uppercase tracking-wider text-[#FFFFFF]/70 font-semibold">
                  {STATUS_LABELS[status]}
                </span>
              </div>
              <span className="text-[11px] text-[#FFFFFF]/45 num">{items.length}</span>
            </header>
            <ul className="space-y-2">
              {items.map((j) => {
                const rightAction = j.customer_phone
                  ? {
                      label: "Call",
                      icon: Phone,
                      tone: "emerald",
                      onTrigger: () => { window.location.href = `tel:${j.customer_phone}`; },
                    }
                  : null;
                const leftAction =
                  j.status === "new" || j.status === "scheduled"
                    ? {
                        label: "En-route",
                        icon: Truck,
                        tone: "amber",
                        onTrigger: () => flipStatus(j.id, "en-route", "Marked en-route"),
                      }
                    : j.status === "en-route"
                    ? {
                        label: "I've arrived",
                        icon: MapPinned,
                        tone: "blue",
                        onTrigger: () => flipStatus(j.id, "on-site", "Marked on-site"),
                      }
                    : null;
                return (
                  <li key={j.id}>
                    <SwipeCard
                      onOpen={() => onOpenJob(j.id)}
                      right={rightAction}
                      left={leftAction}
                      testid={`tech-job-${j.id}`}
                    >
                      <div className="p-3.5 hover:border-[#FF6600] hover:shadow-sm transition-all">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold capitalize">{j.appliance}</span>
                              {j.manager_flag && <Flag className="w-3 h-3 text-[#FF6600]" />}
                            </div>
                            <div className="text-sm text-[#FFFFFF] mt-0.5 line-clamp-1">{j.customer_name}</div>
                            <div className="flex items-start gap-1.5 text-xs text-[#FFFFFF]/70 mt-1">
                              <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                              <span className="line-clamp-1">{j.address}</span>
                            </div>
                            {j.scheduled_for && (
                              <div className="flex items-center gap-1.5 text-xs text-[#FFFFFF]/70 mt-0.5">
                                <Clock className="w-3 h-3" /> {j.scheduled_for}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-4 h-4 text-[#FFFFFF]/45 shrink-0 mt-1" />
                        </div>
                      </div>
                    </SwipeCard>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </>
  );
}

function Tile({ label, value, tone = "slate", testid }) {
  const colors = {
    slate: "text-[#FFFFFF]",
    amber: "text-[#FF6600]",
    emerald: "text-[#FFFFFF]",
  };
  return (
    <div className="bg-[#002060] border border-[#FFFFFF]/15 rounded-lg p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-[#FFFFFF]/70 font-medium">{label}</div>
      <div className={`display text-xl font-semibold num mt-1 ${colors[tone]}`}>{value}</div>
    </div>
  );
}
